import React, { useEffect, useState } from "react";
import AppShell, { PageContainer, PageHeader } from "@/components/AppShell";
import { api } from "@/lib/api";
import { useT } from "@/lib/i18n";
import { Button } from "@/components/ui/button";
import { Sparkles, User, Users, Gauge, Coffee, Clock, Timer } from "lucide-react";

export default function WhatToPlayPage() {
  const { t } = useT();

  const MODE_OPTIONS = [
    { value: "solo", label: t("page.play.solo"), icon: User, desc: t("page.play.solo_desc") },
    { value: "team", label: t("page.play.team"), icon: Users, desc: t("page.play.team_desc") },
  ];
  const INTENSITY_OPTIONS = [
    { value: "relaxed", label: t("page.play.relaxed"), icon: Coffee, desc: t("page.play.relaxed_desc") },
    { value: "competitive", label: t("page.play.competitive"), icon: Gauge, desc: t("page.play.competitive_desc") },
  ];
  const SESSION_OPTIONS = [
    { value: "short", label: t("page.play.short"), icon: Timer, desc: t("page.play.short_desc") },
    { value: "long", label: t("page.play.long"), icon: Clock, desc: t("page.play.long_desc") },
  ];

  const [pref, setPref] = useState({ mode: "team", intensity: "competitive", session: "short", platform: "any" });
  const [platforms, setPlatforms] = useState([]);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/meta/platforms").then((r) => setPlatforms(r.data.items));
  }, []);

  const recommend = async () => {
    setLoading(true);
    try {
      const res = await api.post("/recommend", pref);
      setResults(res.data.items);
    } finally {
      setLoading(false);
    }
  };

  return (
    <AppShell>
      <PageContainer>
        <PageHeader
          kicker={t("page.play.kicker")}
          title={t("page.play.title")}
          subtitle={t("page.play.sub")}
        />

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6">
          <PickerGroup
            label={t("page.play.mode")}
            options={MODE_OPTIONS}
            value={pref.mode}
            onChange={(v) => setPref({ ...pref, mode: v })}
            testidPrefix="play-mode"
          />
          <PickerGroup
            label={t("page.play.intensity")}
            options={INTENSITY_OPTIONS}
            value={pref.intensity}
            onChange={(v) => setPref({ ...pref, intensity: v })}
            testidPrefix="play-intensity"
          />
          <PickerGroup
            label={t("page.play.session")}
            options={SESSION_OPTIONS}
            value={pref.session}
            onChange={(v) => setPref({ ...pref, session: v })}
            testidPrefix="play-session"
          />
        </div>

        <div className="mt-6 rounded-2xl bg-zinc-900/50 border border-white/5 p-4 flex flex-wrap items-center gap-3">
          <span className="text-sm text-zinc-400">{t("page.play.platform")}</span>
          <div className="flex flex-wrap gap-2">
            {[{ code: "any", name: t("page.play.any") }, ...platforms].map((p) => (
              <button
                key={p.code}
                onClick={() => setPref({ ...pref, platform: p.code })}
                data-testid={`play-platform-${p.code}`}
                className={`px-3 py-1.5 rounded-full text-xs font-semibold border min-h-[36px] ${
                  pref.platform === p.code
                    ? "bg-neon text-black border-neon"
                    : "bg-white/5 text-zinc-300 border-white/10 hover:border-white/20"
                }`}
              >
                {p.name}
              </button>
            ))}
          </div>
          <Button
            className="ml-auto w-full sm:w-auto h-12 rounded-full bg-neon text-black hover:bg-neon-hover font-bold shadow-neon"
            onClick={recommend}
            disabled={loading}
            data-testid="play-recommend-btn"
          >
            <Sparkles size={16} className="mr-2" />
            {loading ? t("page.play.thinking") : t("page.play.recommend")}
          </Button>
        </div>

        {results.length > 0 && (
          <div className="mt-8">
            <h2 className="font-display text-2xl font-bold mb-4">{t("page.play.top_picks")}</h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
              {results.map((g, i) => (
                <article
                  key={g.id}
                  className="card-interactive rounded-2xl overflow-hidden border border-white/5 bg-zinc-900/60 animate-fade-up"
                  style={{ animationDelay: `${i * 60}ms` }}
                  data-testid={`rec-game-${g.id}`}
                >
                  <img src={g.cover} alt={g.name} className="w-full h-28 object-cover" />
                  <div className="p-3">
                    <div className="font-display font-bold text-sm">{g.name}</div>
                    <div className="text-[11px] text-zinc-500">{g.genre}</div>
                  </div>
                </article>
              ))}
            </div>
          </div>
        )}
      </PageContainer>
    </AppShell>
  );
}

function PickerGroup({ label, options, value, onChange, testidPrefix }) {
  return (
    <div className="rounded-2xl bg-zinc-900/50 border border-white/5 p-4">
      <div className="text-xs uppercase tracking-widest text-zinc-500 font-mono mb-3">{label}</div>
      <div className="grid grid-cols-2 gap-2">
        {options.map((o) => {
          const Icon = o.icon;
          const active = value === o.value;
          return (
            <button
              key={o.value}
              onClick={() => onChange(o.value)}
              data-testid={`${testidPrefix}-${o.value}`}
              className={`rounded-xl border p-3 text-left transition-all min-h-[44px] ${
                active ? "border-neon bg-neon/10 shadow-neon" : "border-white/10 hover:border-white/20 bg-zinc-950/50"
              }`}
            >
              <Icon className={`${active ? "text-neon" : "text-zinc-400"}`} size={18} />
              <div className="mt-2 font-bold text-sm">{o.label}</div>
              <div className="text-[11px] text-zinc-500">{o.desc}</div>
            </button>
          );
        })}
      </div>
    </div>
  );
}
